import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Droplet, Wrench, Cog, Activity, Circle, Disc, SprayCan, ClipboardCheck,
  ArrowRight, ScanLine, ShieldCheck, Zap, Clock, Truck, Lightbulb,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CarteGriseScanner } from "@/components/CarteGriseScanner";
import { EmergencyDepannage } from "@/components/EmergencyDepannage";
import { ReviewsSection } from "@/components/ReviewsSection";
import { HeroRating } from "@/components/HeroRating";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { MobileTabBar } from "@/components/MobileTabBar";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const ICONS = { droplet: Droplet, wrench: Wrench, cog: Cog, activity: Activity, circle: Circle, disc: Disc, "spray-can": SprayCan, "clipboard-check": ClipboardCheck, zap: Zap, truck: Truck, lightbulb: Lightbulb };
const TRACK_ONLY = ["electronic-auto"];

const HERO_IMG = "https://images.unsplash.com/photo-1727893304219-063d142ce6f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHw0fHxjYXIlMjBpbiUyMG1vZGVybiUyMGJyaWdodCUyMGF1dG8lMjByZXBhaXIlMjB3b3Jrc2hvcCUyMG5vJTIwcGVvcGxlfGVufDB8fHx8MTc4NTYxMTY4OXww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);

  const sLabel = (s) => { const k = `services.${s.id}.label`; const v = t(k); return v === k ? s.label : v; };
  const sDesc = (s) => { const k = `services.${s.id}.desc`; const v = t(k); return v === k ? (s.desc || "") : v; };
  const sDur = (s) => {
    const d = (s.duration || "").trim();
    if (!d || d === "Variable") return t("services.variable");
    if (/24\s*[h/]/i.test(d) || /7\s*\/\s*7/i.test(d)) return t("services.always");
    return d;
  };
  const sPrice = (s) => {
    const p = (s.price || "").trim();
    if (!p) return "";
    if (/sur devis|op offerte|on quote/i.test(p)) return t("services.on_quote");
    return p.replace(/^d[eè]s\s+/i, `${t("services.price_from")} `);
  };
  const fillerCount = services.length ? (3 - (services.length % 3)) % 3 : 0;

  return (
    <div className="noise-overlay bg-[#050505] min-h-screen pb-16 md:pb-0">
      <Navbar />

      {/* HERO */}
      <section className="hero-dark relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" className="hero-img w-full h-full object-cover" />
          <div className="hero-veil-dark absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/10" />
          <div className="hero-veil-dark absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/20 to-transparent" />
          <div className="hero-veil-light absolute inset-0" />
          <div className="absolute inset-0 grid-bg opacity-40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
              className="font-display font-900 text-white text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.9] uppercase"
            >
              {t("hero.title1")}
              <br />
              <span className="text-[#E60000]">{t("hero.title2")}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
              className="text-zinc-400 text-base sm:text-lg mt-7 max-w-xl leading-relaxed"
            >
              {t("hero.desc_pre")}<span className="text-white font-semibold">Garage Ter Spelt</span>{t("hero.desc_post")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 mt-10"
            >
              <Link
                to="/booking" data-testid="hero-booking-btn"
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors"
              >
                {t("hero.cta_book")}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <button
                onClick={() => document.getElementById("scan")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-scan-btn"
                className="group inline-flex items-center justify-center gap-2.5 px-7 py-4 bg-[#00E5FF]/10 text-[#00E5FF] font-display font-700 uppercase tracking-wide border border-[#00E5FF] shadow-[0_0_14px_rgba(0,229,255,0.25)] hover:bg-[#00E5FF]/20 hover:shadow-[0_0_26px_rgba(0,229,255,0.5)] transition-all"
              >
                <ScanLine className="w-5 h-5 group-hover:scale-110 transition-transform" />
                {t("hero.cta_scan")}
              </button>
            </motion.div>

            <HeroRating />

            <div className="flex flex-wrap gap-x-10 gap-y-4 mt-14">
              {[
                { icon: Zap, label: t("hero.f_diag") },
                { icon: ShieldCheck, label: t("hero.f_cert") },
                { icon: Clock, label: t("hero.f_247") },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <f.icon className="w-4 h-4 text-[#E60000]" />
                  <span className="font-mono-tech text-xs uppercase tracking-[0.12em] text-zinc-400">{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 02 — SCAN IA */}
      <section id="scan" className="relative py-16 sm:py-20 border-t border-white/10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{ backgroundImage: `url(https://images.pexels.com/photos/28901526/pexels-photo-28901526.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#00E5FF]">{t("scan.overline")}</span>
            <h2 className="font-display font-900 text-white text-4xl sm:text-5xl tracking-tighter uppercase mt-3 leading-[0.95]">
              {t("scan.title1")}
              <br />
              <span className="text-[#E60000]">{t("scan.title2")}</span>
            </h2>
            <p className="text-zinc-400 mt-6 leading-relaxed">{t("scan.desc")}</p>
            <ul className="mt-8 space-y-4">
              {[t("scan.li1"), t("scan.li2"), t("scan.li3")].map((txt, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-zinc-300">
                  <span className="w-1.5 h-1.5 bg-[#00E5FF] shadow-[0_0_8px_#00E5FF]" />
                  {txt}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <CarteGriseScanner />
          </div>
        </div>
      </section>

      {/* 03 — SERVICES */}
      <section id="services" className="relative py-16 sm:py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#00E5FF]">{t("services.overline")}</span>
              <h2 className="font-display font-900 text-white text-4xl sm:text-5xl tracking-tighter uppercase mt-3">{t("services.title")}</h2>
            </div>
            <p className="text-zinc-500 max-w-sm text-sm leading-relaxed">{t("services.subtitle")}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {services.map((s, i) => {
              const isDep = /d[eé]pannage/i.test(s.id) || /d[eé]pannage/i.test(sLabel(s));
              const Icon = isDep ? Truck : (ICONS[s.icon] || Wrench);
              return (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                  data-testid={`service-card-${s.id}`}
                  onClick={() => navigate("/booking", { state: { service: s.id } })}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/booking", { state: { service: s.id } }); } }}
                  className="group bg-[#0C0C0E] p-7 hover:bg-[#121215] transition-colors relative cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-[#00E5FF]"
                >
                  {s.price && <span data-testid={`service-price-${s.id}`} className="absolute top-6 right-6 px-2.5 py-1 border border-[#00E5FF]/30 bg-[#00E5FF]/10 text-[#00E5FF] font-mono-tech text-[11px] tracking-wider">{sPrice(s)}</span>}
                  <div className="w-12 h-12 flex items-center justify-center border border-white/10 group-hover:border-[#E60000] group-hover:bg-[#E60000]/10 transition-colors mb-6">
                    <Icon className="w-5 h-5 text-[#E60000]" />
                  </div>
                  <h3 className="font-display font-700 text-white text-lg tracking-tight">{sLabel(s)}</h3>
                  <p className="text-zinc-500 text-sm mt-2 leading-relaxed min-h-[60px]">{sDesc(s)}</p>
                  {TRACK_ONLY.includes(s.id) && (
                    <p className="flex items-start gap-1.5 text-amber-400/80 text-[10px] leading-snug mt-1 mb-1" data-testid={`service-trackonly-${s.id}`}>
                      <span className="mt-px">⚠</span>{t("booking.track_only")}
                    </p>
                  )}
                  {isDep && (
                    <div onClick={(e) => e.stopPropagation()} className="mt-2">
                      <span className="inline-flex items-center gap-1.5 mb-2 px-2.5 py-1 border border-[#E60000]/40 bg-[#E60000]/10 text-[#E60000] font-mono-tech text-[11px] uppercase tracking-wider" data-testid="depannage-availability">
                        <Clock className="w-3 h-3" />{t("depannage.availability")}
                      </span>
                      <p className="text-[#E60000] text-xs mb-2 font-mono-tech">{t("depannage.card_sub")}</p>
                      <EmergencyDepannage variant="card" />
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                    <span className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-600">
                      <Clock className="w-3 h-3 inline mr-1.5 -mt-0.5" />{sDur(s)}
                    </span>
                    <span aria-hidden className="text-[#00E5FF] opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`service-book-${s.id}`}>
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </motion.div>
              );
            })}
            {Array.from({ length: fillerCount }).map((_, i) => (
              <Link
                key={`filler-${i}`}
                to="/booking"
                data-testid={`service-filler-${i}`}
                className="group bg-[#0A0A0C] p-7 relative overflow-hidden hidden lg:flex flex-col items-center justify-center text-center hover:bg-[#101013] transition-colors"
              >
                <div className="absolute inset-0 grid-bg opacity-20" />
                <div className="relative z-10">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center border border-white/10 group-hover:border-[#00E5FF] group-hover:bg-[#00E5FF]/10 transition-colors mb-5">
                    <Wrench className="w-5 h-5 text-[#00E5FF]" />
                  </div>
                  <h3 className="font-display font-700 text-white text-lg tracking-tight">{t("services.filler_title")}</h3>
                  <p className="text-zinc-500 text-sm mt-2 leading-relaxed">{t("services.filler_desc")}</p>
                  <span className="inline-flex items-center gap-1.5 mt-4 font-mono-tech text-[11px] uppercase tracking-[0.15em] text-[#00E5FF]">
                    {t("services.filler_cta")} <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* AVIS CLIENTS */}
      <ReviewsSection />

      {/* CTA */}
      <section className="relative py-16 sm:py-20 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-display font-900 text-white text-4xl sm:text-6xl tracking-tighter uppercase leading-none">
            {t("cta.title1")}
            <br />
            <span className="text-[#E60000]">{t("cta.title2")}</span>
          </h2>
          <p className="text-zinc-400 mt-6 max-w-md mx-auto leading-relaxed">{t("cta.subtitle")}</p>
          <Link
            to="/booking" data-testid="cta-booking-btn"
            className="group inline-flex items-center justify-center gap-2 px-9 py-4 mt-10 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors"
          >
            {t("cta.button")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
      <MobileTabBar />
    </div>
  );
}
